import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../api/axios";
import { getArcadeEntriesRoute } from "../../api/routes";
import { CampaignContext } from "../../contexts/CampaignContext";
import { convertMSToHMS, getTzLongDateString } from "../../helpers/Dates";
import { IconJsxer } from "../../helpers/IconHelper";
import { parseTimezone } from "../../helpers/TimeZones";
import LottieIcon from "../common/LottieIcon";
import "./ArcadeDataStreamsDropdown.scss";

const ArcadeDataStreamsDropdown = ({ isOpen, onClose, onSelectStream }) => {
    const [campaignContext] = useContext(CampaignContext);
    const [loading, setLoading] = useState(false);
    const [streamsWithData, setStreamsWithData] = useState([]);

    const campaignFeatures = campaignContext.campaignData?.features || [];
    const streams = useMemo(() => {
        return [...(campaignContext.campaignData?.streams || [])]
            .filter((stream) => !stream.archived)
            .sort((a, b) => b.startTime - a.startTime);
    }, [campaignContext.campaignData?.streams]);

    const normalizeId = (value) => String(value ?? '');

    const getFeatureMeta = (entry) => {
        const campaignFeature = campaignFeatures.find((feature) => {
            return normalizeId(feature._id) === normalizeId(entry.campaignFeatureId)
                || normalizeId(feature.feature?._id) === normalizeId(entry.featureID);
        });
        const storedFeature = campaignFeature?.feature || null;
        return {
            id: normalizeId(entry.campaignFeatureId || entry.featureID),
            label: entry.campaignFeatureName || storedFeature?.featureName || 'Feature',
            iconKey: campaignFeature?.configKey || campaignFeature?.varKey || storedFeature?.varKey || storedFeature?.featureType || null,
        };
    };

    useEffect(() => {
        if (!isOpen || !campaignContext.campaignData?._id) {
            return;
        }

        let cancelled = false;
        setLoading(true);

        Promise.all(
            streams.map((stream) => {
                return axios.get(getArcadeEntriesRoute, {
                    params: {
                        streamID: stream._id,
                    }
                }).then((response) => {
                    const entries = Array.isArray(response.data) ? response.data : [];
                    const features = [];
                    const seenFeatureIds = new Set();
                    entries.forEach((entry) => {
                        const featureMeta = getFeatureMeta(entry);
                        if (!seenFeatureIds.has(featureMeta.id)) {
                            seenFeatureIds.add(featureMeta.id);
                            features.push(featureMeta);
                        }
                    });

                    return {
                        stream,
                        count: entries.length,
                        features,
                    };
                }).catch(() => null);
            })
        ).then((results) => {
            if (cancelled) {
                return;
            }
            setStreamsWithData(results.filter((result) => result && result.count > 0));
            setLoading(false);
        }).catch(() => {
            if (!cancelled) {
                setStreamsWithData([]);
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
        // The dropdown data should refresh only when reopened or campaign streams change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, campaignContext.campaignData?._id, streams]);

    if (!isOpen) {
        return null;
    }

    const formatStreamDate = (stream) => {
        const timezoneData = parseTimezone(stream.timeZone);
        const startTime = convertMSToHMS(stream.startTime, false, timezoneData.offset);
        const endTime = convertMSToHMS(stream.endTime, false, timezoneData.offset);
        return `${getTzLongDateString(stream.startTime, timezoneData.offset)} ${startTime.h_str}:${startTime.m_str} - ${endTime.h_str}:${endTime.m_str}`;
    };

    return (
        <>
            {createPortal(
                <div className="arcade-data-streams-dropdown-overlay" onClick={onClose}></div>,
                document.body
            )}
            <div className="arcade-data-streams-dropdown">
                <div className="arcade-data-streams-dropdown-header">
                    <h3>Live Data</h3>
                    <button className="close-button" onClick={onClose}>
                        {IconJsxer.GetIcon(IconJsxer.ICONS.closeX)}
                        <span>Close</span>
                    </button>
                </div>
                <div className="arcade-data-streams-list">
                    {loading && (
                        <div className="arcade-data-streams-empty">Loading captured data...</div>
                    )}
                    {!loading && streamsWithData.length === 0 && (
                        <div className="arcade-data-streams-empty">No captured live data found for this campaign.</div>
                    )}
                    {!loading && streamsWithData.map(({ stream, count, features }) => (
                        <button
                            type="button"
                            key={stream._id}
                            className="arcade-data-stream-item"
                            onClick={() => onSelectStream(stream._id)}
                        >
                            <div className="arcade-data-stream-main">
                                <div className="arcade-data-stream-title-row">
                                    <h4>{stream.name || stream.twitchHandle || 'Unnamed Stream'}</h4>
                                    <span className="arcade-data-count">{count} capture{count === 1 ? '' : 's'}</span>
                                </div>
                                <div className="arcade-data-stream-subtitle">{formatStreamDate(stream)}</div>
                                <div className="arcade-data-feature-badges">
                                    {features.map((feature) => (
                                        <div className="arcade-data-feature-badge" key={`${stream._id}_${feature.id}`} title={feature.label}>
                                            <div className="arcade-data-feature-badge-icon">
                                                <LottieIcon varKey={feature.iconKey} size={20} />
                                            </div>
                                            <span>{feature.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="arcade-data-stream-arrow">
                                {IconJsxer.GetIcon(IconJsxer.ICONS.chevronRight, IconJsxer.ICON_STYLES.standard)}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </>
    );
};

export default ArcadeDataStreamsDropdown;